import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "../api/client";

/**
 * Belt-and-braces guard for pages that guests can't access (pack
 * opening, deck builder). The lobby buttons already route guests to
 * `/convert?reason=…`, but this hook also catches deep-links / hard
 * refreshes / late-arriving session changes by polling `/me` once on
 * mount.
 *
 * The server is authoritative — `RequireFullUser` 403s the underlying
 * API calls — so this hook is purely a UX nicety to land guests on the
 * conversion page instead of a half-rendered page that shows an error.
 */
export function useGuestRedirect(reason: "packs" | "deck"): void {
  const navigate = useNavigate();
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await api.getMe();
        if (cancelled) return;
        if (me.user.is_guest) {
          navigate(`/convert?reason=${reason}`, { replace: true });
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof api.ApiError && err.status === 401) {
          navigate("/login", { replace: true });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, reason]);
}
